import { GOOGLE_LANGUAGES, LANGUAGE_CODES } from '@techchange/i18n';

/**
 * The default language for the platform.
 */
export const DEFAULT_LANGUAGE = 'en';

/**
 * The supported ISO-639-1 langage codes for the platform,
 * i.e. languages for which we have translations.
 *
 * Note that Farsi - Persian is not yet a supported platform language
 * and only has partial translations.
 */
export const TRANSLATED_LANGUAGES = [
	'ar', // Arabic
	'en', // English (default)
	'es', // Spanish
	// 'fa', // Farsi - Persian
	'fr', // French
	'hi', // Hindi
	'pt', // Portuguese
	'ro', // Romanian
	'sw', // Swahili
];

/**
 * The languages that are read right to left.
 */
export const RIGHT_TO_LEFT_LANGUAGES = {
	ar: true, // Arabic
	fa: true, // Farsi - Persian
};

export const LEFT_TO_RIGHT = 'ltr';
export const RIGHT_TO_LEFT = 'rtl';

/**
 * Select options for languages that currently have partial or complete
 * UI translations available on the platform.
 *
 * @param {Object}  [options]  Function options.
 * @param {Boolean} [options.useNativeName]
 * 	Flag indicating whether to use the native name of the language as the option label.
 * 	The English name of the language will be used by default.
 *
 * @returns {Array}
 * 	The select options for languages that are supported on the platform.
 * 	Each option has a 'value' and 'label' field. The 'value'
 * 	field is the ISO-639-1 code for the language and the 'label'
 * 	field is the name of the language.
 *
 */
export const getLanguageOptions = (options = {}) => {

	return TRANSLATED_LANGUAGES.map((languageCode) => {

		const label = options.useNativeName
			? LANGUAGE_CODES[languageCode].nativeName
			: LANGUAGE_CODES[languageCode].name;

		return {
			label,
			value: languageCode,
		};

	});

};

/**
 * Select options for languages with automated translation support.
 *
 * @param {Object}  [options]  Function options.
 * @param {Boolean} [options.useNativeName]
 * 	Flag indicating whether to use the native name of the language as the option label.
 * 	The English name of the language will be used by default.
 *
 * @returns {Array}
 * 	The select options for languages with support for automated translations.
 * 	Each option has a 'value' and 'label' field. The 'value'
 * 	field is the ISO-639 code for the language and the 'label'
 * 	field is the name of the language. Options are sorted alphabetically according
 * 	to the English name of the language.
 */
export const getAutoTranslateLanguageOptions = (options = {}) => {
	return GOOGLE_LANGUAGES.map((languageCode) => {

		const label = options.useNativeName
			? LANGUAGE_CODES[languageCode].nativeName
			: LANGUAGE_CODES[languageCode].name;

		return {
			value: languageCode,
			label,
		};

	}).sort((lang1, lang2) => {

		const language1Name = LANGUAGE_CODES[lang1.value].name;
		const language2Name = LANGUAGE_CODES[lang2.value].name;

		return language1Name.localeCompare(language2Name, 'en');

	});
};

/**
 * Select options for all ISO-639-1 languages.
 *
 * Each option has a 'value' and 'label' field. The 'value'
 * field is the ISO-639-1 code for the language and the 'label'
 * field is the native name of the language.
 */

export const globalLanguageOptions =
Object.keys(LANGUAGE_CODES).map((languageCode) => ({
	value: languageCode,
	label: `${LANGUAGE_CODES[languageCode].name} (${LANGUAGE_CODES[languageCode].nativeName})`,
})).sort((lang1, lang2) => {

	const language1Name = LANGUAGE_CODES[lang1.value].name;
	const language2Name = LANGUAGE_CODES[lang2.value].name;

	return language1Name.localeCompare(language2Name, 'en');

});

/**
 * Normalizes locale names and filters only supported locales
 *
 * @param {Array} locales  The array of locales to be normalized
 *
 * @returns {Array}  The array of normalized locale names
 */
function normalizeLocales(locales) {

	const languageOptionValues = getLanguageOptions().map((option) => option.value);

	return locales.map((locale) => {

		if (!locale) {
			return locale;
		}

		// Language setting may specify locality (e.g. "en-US"),
		// but we only use the language code for now
		const splitLocale = locale.split('-');

		return splitLocale[0];

	}).filter((lang) => languageOptionValues.includes(lang));

}

/**
 * Determines the user's language setting from the browser, if any.
 *
 * @returns {String}  The user's browser language if it matches the list of language options
 *                    supported by the platform.
 */
function getBrowserLanguage() {

	let languageList = [];

	try {

		if (typeof window !== 'undefined') {

			const { navigator } = window;

			if (navigator?.languages) {
				languageList = languageList.concat(navigator.languages);
			}

			if (navigator?.language) {
				languageList.push(navigator.language);
			}

			if (navigator?.userLanguage) {
				languageList.push(navigator.userLanguage);
			}

			if (navigator?.browserLanguage) {
				languageList.push(navigator.browserLanguage);
			}

			if (navigator?.systemLanguage) {
				languageList.push(navigator.systemLanguage);
			}

		}

		const locales = normalizeLocales([...new Set(languageList)]);

		return locales[0];

	} catch (error) {

		return null;

	}

}

/**
 * Determines the preferred locale of the current user given
 * user, cookie, browser, product, and organization settings in that order.
 *
 * @param {Object} currentUser     The current user.
 * @param {Object} myOrganization  The current organization.
 * @param {Object} [product]       The current product (if any).
 * @param {Object} cookies         The browser cookies.
 *
 * @returns {String} The preferred locale based on all user preferences
 */
export function getPreferredLocale(currentUser, myOrganization, product, cookies) {

	return (
		currentUser?.language ||
		cookies?.language ||
		getBrowserLanguage() ||
		product?.language ||
		myOrganization?.language ||
		DEFAULT_LANGUAGE
	);

}
